import { useEffect, useState } from 'react';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type {
  EnhancedGLTF,
  LoadedCharacterMesh,
  MorphIdentity,
} from '@cartwhl/3d/gltf/types';
import {
  applyIdentityHeightScale,
  applyIdentityJointScales,
  applyIdentityRestBoneScales,
  applyMorphShapes,
  detectMhr,
} from '@cartwhl/3d/gltf/utils';

import { inferMeshFormatFromPath } from '../characterMeshFormat';
import { loadAndEnrichFBX } from './loadCharacterMesh';
import {
  createFbxCharacterMeshTransform,
  loadFbxCharacterMesh,
} from './loadFbxCharacterMesh';
import { loadAndEnrichGLTF } from './useGLTFWithCredentials';

export type CharacterMeshFormat = 'fbx' | 'glb' | 'gltf';

function inferFormat(
  path: string,
  hint?: CharacterMeshFormat,
): CharacterMeshFormat {
  if (hint) return hint;
  return inferMeshFormatFromPath(path) ?? 'glb';
}

export type LoadedCharacter = EnhancedGLTF | LoadedCharacterMesh;
export type TransformLoadedMesh = (
  model: LoadedCharacter,
) => void | Promise<void>;
export type LoadFBXMesh = (path: string) => Promise<LoadedCharacter>;

/**
 * Load a character mesh (GLB/GLTF or FBX) with the same credential and
 * caching behavior for both. Dispatches by path extension or optional format hint
 * (required for blob URLs which have no extension).
 */
export default function useCharacterMeshWithCredentials(
  path: string | undefined,
  setLoading?: (loading: boolean) => void,
  options?: {
    /** Required when path is a blob URL (no extension). */
    format?: CharacterMeshFormat;
    /** Optional modifiers applied only for GLTF/GLB. */
    modifiers?: Array<(model: GLTF) => void>;
    /**
     * When true, fetches the sidecar bones `.json` like `useGLTFWithCredentials`.
     * Character mesh loading defaults to false to avoid extra requests.
     */
    loadBonesMap?: boolean;
    /** Optional pre-render mutation hook for loaded FBX/GLTF scenes. */
    transformLoadedMesh?: TransformLoadedMesh;
    /** Optional FBX loader override for callers that need a specialized parser. */
    loadFBX?: LoadFBXMesh;
    /** Optional URL to static morph identity JSON; applied once after load (MHR rigs only). */
    identityPath?: string;
  },
): LoadedCharacter | null {
  const [model, setModel] = useState<LoadedCharacter | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!path) return;

    const format = inferFormat(path, options?.format);
    const transformLoadedMesh =
      options?.transformLoadedMesh ??
      (format === 'fbx'
        ? createFbxCharacterMeshTransform({ meshPath: path })
        : undefined);
    const loadFBX = options?.loadFBX ?? loadFbxCharacterMesh;
    setLoading?.(true);

    let cancelled = false;

    const load = (async () => {
      const meshPromise =
        format === 'fbx'
          ? loadFBX(path)
          : loadAndEnrichGLTF(path, options?.modifiers, {
              loadBonesMap: options?.loadBonesMap ?? false,
            });
      const identityPath = options?.identityPath;
      const identityPromise: Promise<MorphIdentity | null> = identityPath
        ? fetch(identityPath, {
            credentials: /^https?:\/\//i.test(identityPath)
              ? 'omit'
              : 'include',
          })
            .then((r) => (r.ok ? (r.json() as Promise<MorphIdentity>) : null))
            .catch(() => null)
        : Promise.resolve(null);
      const [data, identity] = await Promise.all([
        meshPromise,
        identityPromise,
      ]);
      if (transformLoadedMesh) {
        await transformLoadedMesh(data);
      }
      const extras =
        format !== 'fbx'
          ? ((
              data as unknown as {
                asset?: { extras?: Record<string, unknown> };
              }
            ).asset?.extras as { rig_type?: string } | undefined)
          : undefined;
      const isMhr = detectMhr(extras);
      const sceneUserData = data.scene.userData as Record<string, unknown>;
      sceneUserData.isMhr = isMhr;
      if (identity) {
        if (isMhr) {
          applyMorphShapes(data, identity);
          applyIdentityJointScales(data, identity);
          applyIdentityRestBoneScales(data, identity);
          applyIdentityHeightScale(data, identity);
        } else {
          console.warn(
            '[mhr] identityPath provided but mesh is not detected as MHR; skipping identity application',
          );
        }
      }
      return data;
    })();

    load
      .then((data) => {
        if (cancelled) return;
        setModel(data);
        setLoading?.(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setLoading?.(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    path,
    options?.format,
    options?.loadBonesMap,
    options?.transformLoadedMesh,
    options?.loadFBX,
    options?.identityPath,
  ]);

  if (error) throw error;
  return model;
}

export { loadAndEnrichFBX };
